import { Select } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery } from "@msuite/ui/query";
import { WorkspaceTag } from "@msuite/ui/tag";
import { responseHandler } from "@msuite/ui/utils";
import type { Tables } from "@msuite/utils/types";
import { useEffect } from "react";
import type { Control, FieldValues, Path, UseFormReturn } from "react-hook-form";
import { client } from "@/hono";

/** Props Interface */
interface WorkspacePickerProps<T extends FieldValues> {
	methods: UseFormReturn<T>;
	control: Control<T>;
	name: Path<T>;
	selectDefault?: boolean;
}

export function WorkspacePicker<T extends FieldValues>({
	methods,
	control,
	name,
	selectDefault,
}: WorkspacePickerProps<T>) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const { data: workspaces } = useQuery({
		queryKey: ["workspaces"],
		queryFn: async () => {
			const response = await client.organizations.workspaces.$get();
			const data = await responseHandler(response);
			return data.workspaces;
		},
	});

	/** Effects */
	useEffect(() => {
		if (!selectDefault) return;
		function setDefaultWorkspace() {
			if (!methods.getValues(name) && selectDefault) {
				const defaultWorkspace = workspaces?.at(0);
				if (defaultWorkspace) {
					methods.setValue(name, defaultWorkspace.id as any);
				}
			}
		}
		setDefaultWorkspace();
	}, [workspaces, name, selectDefault]);

	/** Render */
	return (
		<Select
			control={control}
			name={name}
			label={t("organization.workspace")}
			options={
				workspaces?.map((workspace: Tables<"workspaces">) => ({
					label: workspace.name,
					value: workspace.id,
					item: workspace,
				})) ?? []
			}
			renderOption={(item) => <WorkspaceTag workspace={item} />}
			itemNotFoundLabel={t("organization.workspace_not_found")}
			isRequired
			data-cy="workspace-picker"
			isNullable={false}
		/>
	);
}
